import { DriveMode, DriveModeKey } from '@/utils/constant';
import { CockpitRealtimeErrorCode } from '@/utils/enum';
import { stringToEnum, transFormAngle } from '@/utils/util';
import { createSlice, createAction } from '@reduxjs/toolkit';
export const actionCockpitRealtime = createAction<any>('REALTIME_DATA');
export const updateVelocity = createAction<any>('updateVelocity');
export const removeNewInfoTag = createAction<any>('removeNewInfoTag');

export const DrivingMode: any = {
  0: DriveModeKey.UNKNOWN,
  1: DriveModeKey.DRIVEMODE_ENGAGED_INIT,
  2: DriveModeKey.DRIVEMODE_ENGAGED_AUTO_CTRL,
  3: DriveModeKey.DRIVEMODE_TAKEOVER_REMOTE_CTRL,
  4: DriveModeKey.DRIVEMODE_ENGAGED_GUARDIAN_NORMAL,
  5: DriveModeKey.DRIVEMODE_TAKEOVER_XBOX_CTRL,
  6: DriveModeKey.DRIVEMODE_TAKEOVER_CHASSIS_CTRL,
  7: DriveModeKey.DRIVEMODE_TAKEOVER_EMERGENCY_STOP,
  8: DriveModeKey.DRIVEMODE_ENGAGED_GUARDIAN_EMERGENCY,
  9: DriveModeKey.DRIVEMODE_ENGAGED_GUARDIAN_FORBID_DRIVING,
  10: DriveModeKey.DRIVEMODE_ENGAGED_CHASSIS_FAULT,
  11: DriveModeKey.DRIVEMODE_ENGAGED_CHASSIS_STANDBY,
  12: DriveModeKey.DRIVEMODE_ENGAGED_CHASSIS_BUMPER,
  13: DriveModeKey.DRIVEMODE_TAKEOVER_REMOTE_SUPER_CTRL,
  14: DriveModeKey.DRIVEMODE_TAKEOVER_REMOTE_MOBILE_CTRL,
  15: DriveModeKey.DRIVEMODE_ENGAGED_GUARDIAN_COLLISION,
  16: DriveModeKey.DRIVEMODE_TAKEOVER_REMOTE_JOYSTICK_CTRL,
  17: DriveModeKey.DRIVEMODE_TAKEOVER_REMOTE_JOYSTICK_SUPER_CTRL,
};

export enum GearPosition {
  GEAR_UNKNOWN = 0,
  GEAR_NEUTRAL = 1,
  GEAR_DRIVE = 2,
  GEAR_REVERSE = 3,
  GEAR_PARKING = 4,
  GEAR_LOW = 5,
  GEAR_INVALID = 6,
  GEAR_NONE = 7,
  GEAR_SPORT = 8,
}

export enum Operator {
  OPERATOR_UNKNOWN = 0,
  OPERATOR_MOBILE = 1, //移动
  OPERATOR_UNICOM = 2, //联通
  OPERATOR_TELECOM = 3, //电信
}

export enum SignalType {
  SIGNAL_UNKNOWN = 0,
  SIGNAL_2G = 1,
  SIGNAL_3G = 2,
  SIGNAL_4G = 3,
  SIGNAL_5G = 4,
  SIGNAL_WIFI = 5,
}

const GearPositionTranslator = new Map([
  [GearPosition.GEAR_NEUTRAL, 'N'],
  [GearPosition.GEAR_DRIVE, 'D'],
  [GearPosition.GEAR_REVERSE, 'R'],
  [GearPosition.GEAR_PARKING, 'P'],
]);

const OperateText = new Map([
  [Operator.OPERATOR_UNKNOWN, '未知网络'],
  [Operator.OPERATOR_MOBILE, '中国移动'],
  [Operator.OPERATOR_UNICOM, '中国联通'],
  [Operator.OPERATOR_TELECOM, '中国电信'],
]);

const SignalTypeText = new Map([
  [SignalType.SIGNAL_UNKNOWN, ''],
  [SignalType.SIGNAL_2G, '2G'],
  [SignalType.SIGNAL_3G, '3G'],
  [SignalType.SIGNAL_4G, '4G'],
  [SignalType.SIGNAL_5G, '5G'],
  [SignalType.SIGNAL_WIFI, 'wifi'],
]);

type CockpitRealtime = {
  operator: string | undefined;
  signalType: string | undefined;
  signalPower: number;
  networkDeviceNo: string;
  gearPosition: any;
  delay: string;
  drivingMode: any;
  drivingModeKey: any;
  heading: number;
  targetFrontAngle: number | string;
  frontAngle: number | string;
  maxFrontAngle: number | string;
  targetVelocity: number;
  velocity: number;
  maxVelocity: number;
  torque: number;
  targetTorque: number;
  brake: number;
  enableFront: boolean | undefined;
  enableBack: boolean | undefined;
  enableLeft: boolean | undefined;
  enableRight: boolean | undefined;
  headLight: boolean | undefined;
  turnLeft: boolean | undefined;
  turnRight: boolean | undefined;
  warningFlash: boolean | undefined;
  updateTime: null | number;
  errorCode: CockpitRealtimeErrorCode | null;
  maxTorque: number;
};

const initState: CockpitRealtime = {
  operator: OperateText.get(Operator.OPERATOR_UNKNOWN),
  signalType: '',
  signalPower: 0,
  networkDeviceNo: '',
  gearPosition: null,
  delay: '--',
  drivingMode: DriveMode.UNKNOWN,
  drivingModeKey: DriveModeKey.UNKNOWN,
  heading: 0,
  targetFrontAngle: 0,
  maxFrontAngle: 0,
  frontAngle: 0,
  targetVelocity: 0,
  velocity: 0,
  maxVelocity: 0,
  torque: 0,
  targetTorque: 0,
  brake: 0,
  enableFront: true,
  enableBack: true,
  enableLeft: true,
  enableRight: true,
  headLight: undefined,
  turnLeft: undefined,
  turnRight: undefined,
  warningFlash: undefined,
  updateTime: null,
  errorCode: null,
  maxTorque: 0.25,
};

const cockpitState = createSlice({
  name: 'cockpit',
  initialState: initState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(actionCockpitRealtime, (state, action: any) => {
      const res = action.payload || {};
      const { realtimeData } = res;
      if (realtimeData) {
        const { ChassisStatus, ControlStatus, NetworkStatus } = realtimeData;
        const {
          enableFront,
          enableBack,
          enableLeft,
          enableRight,
          frontAngle,
          maxFrontAngle,
          gearPosition,
          velocity,
          maxVelocity,
          drivingMode,
          headLight,
          turnLeft,
          turnRight,
          warningFlash,
          maxTorque,
        } = ChassisStatus || {};
        const { operator, signalType, networkDeviceNo, signalPower } =
          NetworkStatus || {};
        const { errorCode } = ControlStatus || {};
        state.errorCode = errorCode;
        state.maxTorque = parseFloat((maxTorque || 0.25).toFixed(2));
        state.enableFront = enableFront;
        state.enableBack = enableBack;
        state.enableLeft = enableLeft;
        state.enableRight = enableRight;
        state.headLight = headLight;
        state.turnLeft = turnLeft;
        state.turnRight = turnRight;
        state.warningFlash = warningFlash;
        state.frontAngle = transFormAngle(frontAngle) || 0;
        const temp = ControlStatus.axes
          ? ControlStatus.axes[0] * maxFrontAngle
          : 0;
        state.targetFrontAngle = transFormAngle(temp);
        state.maxFrontAngle = transFormAngle(maxFrontAngle) || 5;
        state.gearPosition = GearPositionTranslator.get(gearPosition);
        state.velocity = Math.abs(velocity ?? 0);
        state.maxVelocity = Math.round((maxVelocity ?? 0) * 3.6);
        state.operator = OperateText.get(operator);
        state.signalType = SignalTypeText.get(signalType);
        state.networkDeviceNo = networkDeviceNo;
        state.signalPower = signalPower;
        state.delay = ControlStatus.delay
          ? (ControlStatus.delay / 2).toFixed(1)
          : '--';
        state.drivingMode = stringToEnum(DriveMode, DrivingMode[drivingMode]);
        state.drivingModeKey = stringToEnum(
          DriveModeKey,
          DrivingMode[drivingMode]
        );
        state.updateTime = Date.now();
      }
    });
  },
});

export default cockpitState.reducer;
